import { memo, useCallback } from "react";
import { Icon } from "@iconify/react";
import { MARKIT_DOCS } from "@markit/common.utils";
import {
  FlowCodeCard,
  FlowFoldIntro,
  FlowResultRow,
  FlowSourceRow,
  stageLabel,
  wireConnector,
} from "./SubComponents/IntegrationFlowParts";
import { Colors } from "../../utils/colors";

type AiToolsFoldProps = {
  isDesktopSize: boolean;
};

const MCP_SERVER_URL = "https://api.markitai.com/mcp";

// Renders a monochrome AI tool logo in white so it reads on the dark rows
const whiteLogo = (icon: string) => (
  <Icon icon={icon} height={18} style={{ filter: "brightness(0) invert(1)" }} />
);

const codeLine = (keyName: string, value: string, trailingComma: boolean) => (
  <div style={{ paddingLeft: 16 }}>
    {`"${keyName}": ${value}`}
    {trailingComma ? "," : null}
  </div>
);

const AiToolsFold = (props: AiToolsFoldProps) => {
  const { isDesktopSize } = props;

  const learnMoreOnPress = useCallback(() => {
    window.open(MARKIT_DOCS + "/mcp-overview", "_blank");
  }, []);

  return (
    <div
      style={{
        borderRadius: isDesktopSize ? 24 : 16,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <FlowFoldIntro
        isDesktopSize={isDesktopSize}
        badgeIcon={
          <Icon
            icon="ion:sparkles"
            height={14}
            style={{ color: Colors.BLUE5 }}
          />
        }
        badgeLabel="Connect AI Tools"
        title="Text from Claude or ChatGPT"
        body="Connect Markit to Claude, ChatGPT, and other AI assistants with one URL. Ask them to send texts, manage your audience, and more. You approve every connection with your Markit login."
        ctaLabel="Learn More"
        ctaOnPress={learnMoreOnPress}
        chipPrefix="MCP"
        chipText={MCP_SERVER_URL}
      />
      <div
        className={
          isDesktopSize ? "AlignedRow slide-up" : "ColumnNormal slide-up"
        }
        style={{
          marginTop: isDesktopSize ? 64 : 48,
          gap: isDesktopSize ? 4 : 10,
          alignItems: isDesktopSize ? "flex-start" : "stretch",
        }}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("1 — ASK YOUR AI TOOL", isDesktopSize)}
          <div
            className="ColumnNormal"
            style={{ gap: 12, width: "100%", marginTop: 12 }}
          >
            <FlowSourceRow
              icon={<Icon icon="logos:claude-icon" height={18} />}
              title="Text my VIP list"
              subtitle="Claude"
              time="now"
              highlighted
            />
            <FlowSourceRow
              icon={whiteLogo("logos:openai-icon")}
              title="Who joined this week?"
              subtitle="ChatGPT"
              time="2m"
            />
            <FlowSourceRow
              icon={whiteLogo("logos:codex")}
              title="Summarize my replies"
              subtitle="Codex"
              time="5m"
            />
          </div>
        </div>
        {wireConnector(isDesktopSize)}
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("2 — MARKIT DOES THE WORK", isDesktopSize)}
          <div style={{ marginTop: 12, width: "100%" }}>
            <FlowCodeCard
              label="MCP"
              url="api.markitai.com/mcp"
              footerText="Approved with your Markit login"
            >
              <div>{"{"}</div>
              {codeLine("tool", '"send_text"', true)}
              {codeLine("list", '"VIP"', true)}
              {codeLine("message", '"Friday\'s drop is live 🔥"', true)}
              {codeLine("recipients", "128", false)}
              <div>{"}"}</div>
            </FlowCodeCard>
          </div>
        </div>
        {wireConnector(isDesktopSize)}
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("3 — RESULTS COME BACK", isDesktopSize)}
          <div
            className="ColumnNormal"
            style={{ gap: 12, width: "100%", marginTop: 12 }}
          >
            <FlowResultRow
              logoBg="#3b82f5"
              logoText="V"
              title="VIP list"
              status="128 texts sent"
            />
            <FlowResultRow
              logoBg="#ff4f00"
              logoText="A"
              title="Audience"
              status="42 new subscribers"
            />
            <FlowResultRow
              logoBg="#33a854"
              logoText="I"
              title="Inbox"
              status="12 replies summarized"
            />
            <FlowResultRow
              logoBg="#4a144a"
              logoText="✦"
              title="Your AI tool"
              status="Answer ready"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(AiToolsFold);
